import { queryOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import { Logger } from '@/utils/logging';
import { getStandbyStatusPageState } from '@/utils/standby/standbyEntryUiState';

import type {
  StandbyPublicEntryQueryKey,
  StandbyPublicEntryResponse,
} from './types';

/**
 * Contract per spec (squire-api `feat/SQR-14763-standby-public-entry`):
 * `GET /v1/standby-list/public/entry/:entryId` -> 200 with the raw entry
 * status; unknown / malformed / flag-off all answer a UNIFORM 404
 * (`standby-list.public.unavailable`, the anti-enumeration pattern shared with
 * `auth/start`), never distinguishing "no such entry" from "flag off".
 */
export const STANDBY_PUBLIC_ENTRY_UNAVAILABLE_CODE =
  'standby-list.public.unavailable';

// A malformed uuid is 400'd by the server's `ParseUUIDPipe`; the client treats
// that identically to a 404 (both mean "nothing to show here").
const NOT_FOUND_STATUS_CODES = [400, 404];

export const STANDBY_OFFER_POLL_MS = 15_000;
export const STANDBY_WAITING_POLL_MS = 30_000;

const readErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const { code } = error as { code?: unknown };
  return typeof code === 'string' ? code : undefined;
};

const readStatusCode = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const { statusCode } = error as { statusCode?: unknown };
  return typeof statusCode === 'number' ? statusCode : undefined;
};

/**
 * True for the uniform "entry unavailable" outcome. The API client rejects with
 * the response BODY (not an AxiosError), so the restify code is the primary
 * signal; the 400/404 status is a fallback for transport-shaped errors.
 */
export const isStandbyPublicEntryNotFound = (error: unknown): boolean => {
  if (readErrorCode(error) === STANDBY_PUBLIC_ENTRY_UNAVAILABLE_CODE) {
    return true;
  }
  const status = readStatusCode(error);
  return status !== undefined && NOT_FOUND_STATUS_CODES.includes(status);
};

/**
 * True only for the uniform 404 itself — not a 400, which on an action means
 * the request was bad rather than the entry gone.
 */
export const isStandbyPublicEntryGone = (error: unknown): boolean =>
  readErrorCode(error) === STANDBY_PUBLIC_ENTRY_UNAVAILABLE_CODE ||
  readStatusCode(error) === 404;

/**
 * True when the public read was rate-limited (429 — the shared hourly per-IP
 * ceiling). Retrying only burns the ceiling faster, so it's terminal like a 404;
 * the screen shows the throttled copy instead of the generic retry.
 */
export const isStandbyPublicEntryThrottled = (error: unknown): boolean =>
  readStatusCode(error) === 429;

export const fetchStandbyPublicEntry = async (
  entryId: string,
  signal?: AbortSignal,
): Promise<StandbyPublicEntryResponse> => {
  try {
    const { data } = await apiClient.get<StandbyPublicEntryResponse>(
      `/v1/standby-list/public/entry/${encodeURIComponent(entryId)}`,
      { signal },
    );

    return data;
  } catch (error) {
    // Opaque ids only — the entryId is opaque; never log phone/name (spec).
    Logger.error('Error fetching standby public entry', {
      code: readErrorCode(error),
    });

    throw error;
  }
};

export const standbyPublicEntryQuery = (entryId?: string) =>
  queryOptions<
    StandbyPublicEntryResponse,
    Error,
    StandbyPublicEntryResponse,
    StandbyPublicEntryQueryKey
  >({
    queryKey: ['standby', 'publicEntry', entryId ?? ''],
    // The body depends on who reads it (an owner gets the full phone) while
    // the key does not, so it must never be hydrated into another session.
    meta: { persist: false },
    queryFn: ({ signal }) => {
      if (!entryId) {
        throw new Error('Entry id is required');
      }

      return fetchStandbyPublicEntry(entryId, signal);
    },
    enabled: !!entryId,
    staleTime: 15000,
    // Returning to the tab/app must re-check the entry so a superseded link
    // never lingers on a stale terminal state (spec Behaviour 7).
    refetchOnWindowFocus: true,
    // An offer polls so the countdown/expiry track the server; a waiting entry
    // polls so a new offer shows up without a reload, until the shop closes.
    refetchInterval: (query) => {
      const { data: entry, error } = query.state;
      // A 404/429 keeps the last data, so the status alone would poll on and
      // burn the IP ceiling; both are terminal, as in `retry`.
      if (
        isStandbyPublicEntryNotFound(error) ||
        isStandbyPublicEntryThrottled(error)
      ) {
        return false;
      }
      if (entry?.status === 'notified') return STANDBY_OFFER_POLL_MS;
      const isWaiting =
        entry?.status === 'joined' || entry?.status === 'timed_out';
      return isWaiting && getStandbyStatusPageState(entry).kind !== 'dayEnded'
        ? STANDBY_WAITING_POLL_MS
        : false;
    },
    // A missing/unavailable entry (404) or a rate-limit (429) is terminal —
    // retrying only delays the outcome or burns the IP ceiling faster.
    // Everything else keeps the default 3 retries.
    retry: (failureCount, error) =>
      !isStandbyPublicEntryNotFound(error) &&
      !isStandbyPublicEntryThrottled(error) &&
      failureCount < 3,
  });
